/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useLoadingIndicatorTimer } from '#hooks/UNSAFE_useLoadingIndicatorTimer';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { useId } from '#hooks/UNSAFE_useId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { HTMLAttributesSignalExcluded } from '#utils/UNSAFE_attributeUtils';
import { baseStyles, loadingContainerStyles } from './themes/ProgressiveLoaderStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { Size, sizeToCSS } from '#utils/UNSAFE_size';

type Props = {
  /**
   * Specifies whether the main content is currently loading.
   * The fallback content will be shown if the main content is taking too long to load.
   */
  isLoading: boolean;

  /**
   * The content to be shown when loading.
   */
  fallback: ComponentChildren;

  /**
   * The main content to be shown when not loading.
   */
  children: ComponentChildren;

  /**
   * The role.
   */
  role?: HTMLAttributesSignalExcluded['role'];

  /**
   * The tabIndex.
   */
  tabIndex?: HTMLAttributesSignalExcluded['tabIndex'];

  /**
   * The width of the component.
   */
  width?: Size;

  /**
   * The height of the component.
   */
  height?: Size;

  /**
   * The aria-label of the component.
   * This will be used for the aria-label of the DOM element.
   */
  'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];

  /**
   * One or more ids (separated by spaces) of elements that describe the component.
   * This will be used for the aria-describedby of the DOM element.
   */
  'aria-describedby'?: HTMLAttributesSignalExcluded['aria-describedby'];

  /**
   * One or more ids (separated by spaces) of elements that label the component.
   * This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: HTMLAttributesSignalExcluded['aria-labelledby'];
};

/**
 * Displays the specified fallback content during progressive loading of the main content.
 */
export function ProgressiveLoader({
  isLoading,
  fallback,
  children,
  role,
  tabIndex,
  width,
  height,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy
}: Props) {
  const showLoadingIndicator = useLoadingIndicatorTimer(isLoading);
  const { vis_stateLoading, vis_stateLoaded } = useTranslationBundle<BundleType>(
    '@oracle/oraclejet-preact'
  );
  const descId = useId();

  return isLoading && showLoadingIndicator ? (
    <div
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={
        ariaDescribedBy
          ? [...new Set(ariaDescribedBy.split(/\s+/).concat(descId))].join(' ')
          : descId
      }
      class={classNames([baseStyles, loadingContainerStyles])}
      style={{
        width: width && sizeToCSS(width),
        height: height && sizeToCSS(height)
      }}>
      {fallback}
      <HiddenAccessible id={descId}>{vis_stateLoading()}</HiddenAccessible>
      <LiveRegion type="polite">{vis_stateLoading()}</LiveRegion>
    </div>
  ) : (
    <div
      class={baseStyles}
      style={{
        width: width && sizeToCSS(width),
        height: height && sizeToCSS(height)
      }}>
      {children}
      <LiveRegion type="polite">
        {ariaLabel ? `${ariaLabel}, ${vis_stateLoaded()}` : vis_stateLoaded()}
      </LiveRegion>
    </div>
  );
}
